// UCC tab — continuation chain as a vertical timeline: the original
// UCC-1 first, then continuations/amendments/terminations in filing-date
// order. Purely presentational (design tokens only).

import { calendarDate } from "../common/colorBand";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";

interface ContinuationChainProps {
  filing: UccFilingDto;
  allFilings: UccFilingDto[];
}

export function ContinuationChain({ filing, allFilings }: ContinuationChainProps) {
  // Walk up to the UCC-1 root.
  const byId = new Map(allFilings.map((f) => [f.id, f]));
  let root = filing;
  while (root.parentFilingId && byId.has(root.parentFilingId)) {
    const parent = byId.get(root.parentFilingId);
    if (!parent) break;
    root = parent;
  }
  // Collect descendants in chronological order.
  const descendants = allFilings
    .filter((f) => f.parentFilingId === root.id)
    .sort((a, b) => a.filingDate.localeCompare(b.filingDate));

  const chain = [root, ...descendants];

  return (
    <ol
      className="flex flex-col m-0 list-none p-0"
      data-testid="ucc-continuation-chain"
    >
      {chain.map((f, idx) => {
        const isCurrent = f.id === filing.id;
        const isLast = idx === chain.length - 1;
        return (
          <li key={f.id} className="flex gap-space-3">
            <div className="flex flex-col items-center" aria-hidden>
              <span
                className={`h-3 w-3 rounded-full border-2 shrink-0 mt-space-1 ${
                  isCurrent
                    ? "bg-gold border-gold"
                    : "bg-white-pure border-gray-mid"
                }`}
              />
              {!isLast && <span className="w-px flex-1 bg-border-light" />}
            </div>
            <div
              className={`pb-space-3 ${isLast ? "pb-0" : ""}`}
              data-testid={`ucc-chain-node-${f.id}`}
            >
              <div className="text-small text-onyx">
                <span className="font-display font-bold text-label uppercase tracking-label text-gold-shadow">
                  {f.filingType}
                </span>{" "}
                <span className="font-mono">{f.filingNumber}</span>
                {isCurrent && (
                  <span className="text-caption text-gray-mid"> (this filing)</span>
                )}
              </div>
              <div className="text-caption text-gray-mid">
                {calendarDate(f.filingDate)} · {f.filingJurisdiction}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
